import React from 'react';

/**
 * A filter picker's options and how reading them went (rulebook §12), so the
 * picker never says "nothing matches" about a list it doesn't have yet.
 * `items` stays empty until the first read succeeds.
 */
export type FilterOptionsState<T> = {
    items: T[];
    /** `failed` only when a read failed with nothing to show. */
    status: 'loading' | 'failed' | 'ready';
    onRetry: () => void;
};

/**
 * How a query that still returns the `ApiResponse` envelope (rulebook §10
 * migration) went: a reply that says it failed is a failure as much as a
 * request that did.
 */
export const envelopeOptionsStatus = (query: {
    data?: { success: boolean };
    isError: boolean;
}): FilterOptionsState<unknown>['status'] => {
    if (query.data?.success) {
        return 'ready';
    }
    return query.isError || query.data?.success === false
        ? 'failed'
        : 'loading';
};

/**
 * A filter picker's options, how reading them went, and a retry that reads
 * them again (rulebook §12), held stable so the drawer's pickers re-render
 * only when one of the three changes.
 */
export const useFilterOptionsState = <T>(
    items: T[],
    status: FilterOptionsState<T>['status'],
    refetch: () => unknown,
): FilterOptionsState<T> => {
    const onRetry = React.useCallback(() => {
        refetch();
    }, [refetch]);

    return React.useMemo(
        () => ({ items, status, onRetry }),
        [items, onRetry, status],
    );
};
